import {Controller} from "@hotwired/stimulus";
import {useClickOutside} from "stimulus-use";
import {useZoomListener} from "./mixins/use_zoom_listener.js";
import {useResizeListener} from "./mixins/use_resize_listener.js";
import {bodyOverflowHidden} from "./mixins/process_body_overflow.js";

/**
 * Responsive menu controller
 * - allows you to dynamically show/hide the menu display/hide button based
 *   on the menu width and viewport width
 * - allows to open and close a drop down menu on a button click.
 * Drop down menu can be closed on clicking outside as well.
 *
 *  %nav.invisible{data: { controller: 'responsive-menu' }}
 *
 *    %ul{data: { responsive_menu_target: 'container' }}
 *      %li.main-menu-item Menu item 1
 *      %li.main-menu-item Menu item 2
 *      %li.main-menu-item Menu item 3
 *
 *    %button.block.ml-auto.mr-0{aria: { expanded: 'false', label: 'Menu' },
 *                               data: { action: 'click->responsive-menu#toggle', responsive_menu_target: 'toggler' }
 *
 * Since the layout is divided into multiple elements,
 * the current recommended structure for the main menu is as follows:
 *
 * %div{data: { controller: 'responsive-menu' }}
 *   %nav{data: { toggle_target: 'nav' }}
 *     %div{data: {responsive_menu_target: 'wrapper' }}
 *       %div{data: { responsive_menu_target: 'logo' }}
 *       %div{data: { responsive_menu_target: 'container' }}
 *         %div{ data: { responsive_menu_target: 'navigation' }}
 *             %ul{data: { responsive_menu_target: 'centerBlock' }}
 *               %li...
 *               %li...
 *             %ul{ data: { responsive_menu_target: 'rightBlock' }}
 *               %li...
 *               %li...
 *     %div{data: { responsive_menu_target: 'toggler' }}
 *       %button{ data: { responsive_menu_target: 'togglerButton', action: 'click->responsive-menu#toggle' } }
 *
 * If you change the structure of the main menu,
 * you will probably also need to change the targets
 * and how you work with them.
 */

export default class extends Controller {
  static targets = [
    'wrapper',
    'container',
    'navigation',
    'toggler',
    'togglerButton',
    'logo',
    'centerBlock',
    'rightBlock',
    'navHolder',
  ];

  static values = {
    initialized: Boolean
  }

  classes = {
    expanded: 'expanded',
    collapsed: 'collapsed',
    hidden: 'hidden!',
    invisible: 'invisible'
  }

  togglerHeight = 0;
  scrollBarsObserver = null;
  scrollBarsObserverTimeout = null;

  async connect() {
    super.connect();
    useClickOutside(this);
    useZoomListener(this);
    useResizeListener(this);

    this.clickOutsideListener = this._clickOutsideListener.bind(this);

    await this._loadListener();
  }

  disconnect() {
    super.disconnect();
    this._terminateOSObservation();
    window.removeEventListener('table-sticky-header:change', this.monitorTableChange);
  }

  initializedValueChanged(newValue) {
    if (!newValue) this._setMenuState();
  }

  toggle(event) {
    if (event.currentTarget.ariaExpanded === 'true') {
      this._removeClickOutsideListener();
      this._hideMenu();
    } else {
      this._addClickOutsideListener();
      this._showMenu();
    }
  }

  _loadListener() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", async () => {
        this._setMenuState();
      });
    } else {
      document.removeEventListener("DOMContentLoaded", async () => {
        this._setMenuState();
      });
      this._setMenuState();
    }
  }

  _clickOutsideListener(event) {
    if (!this.togglerButtonTarget || this.togglerButtonTarget.ariaExpanded !== 'true') {
      return;
    }
    if (this.togglerTarget && this.togglerTarget.hasAttribute && this.togglerTarget.hasAttribute('hidden')) {
      return;
    }
    if (event.target.closest && (event.target.closest('[data-controller="responsive-menu"]')
      || event.target.closest('[data-controller="dropdown"]')
      || event.target.closest('[data-responsive-menu-target="togglerButton"]'))) {
      return;
    }

    this._hideMenu();
  }

  _setMenuState() {
    this._terminateOSObservation();

    this.navigationTarget.classList.remove(this.classes.hidden);

    this.containerTarget.classList.remove(this.classes.collapsed);
    this.containerTarget.removeAttribute('hidden');

    /* Make nav and ul invisible to avoid flickering */
    this.containerTarget.classList.add(this.classes.invisible);

    /* Hide toggler to calculate the menu width */
    this._hideToggler();

    /* Expand menu to take measurements */
    this._expandMenu();

    const availableWidth = this._calculateAvailableWidth();

    /* Wait for the next frame to ensure all styles are applied */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        /* Toggler height = menu height */
        this.togglerHeight = this._calculateTogglerHeight();

        /* If menu doesn't fit, collapse it */
        if (availableWidth < 0) {
          this._collapseMenu();
          this._showToggler();
          this._hideMenu();
        }

        /* Reveal menu */
        this.containerTarget.classList.remove(this.classes.invisible);
        this.initializedValue = true;
      });
    });
  }

  _calculateAvailableWidth() {
    const wrapperWidth = this.wrapperTarget.offsetWidth;
    const centerBlockWidth = this.centerBlockTarget.offsetWidth;
    const rightBlockWidth = this.rightBlockTarget.offsetWidth;
    const logoWidth = this.hasLogoTarget ? this.logoTarget.offsetWidth : 0;

    /* Keep some space on the right */
    return wrapperWidth - centerBlockWidth - rightBlockWidth - logoWidth - 20;
  }

  _calculateTogglerHeight() {
   return this.wrapperTarget.getBoundingClientRect().height;
  }

  _expandMenu() {
    this.element.classList.remove(this.classes.collapsed);
    this.element.classList.add(this.classes.expanded);
  }

  _collapseMenu() {
    this.element.classList.remove(this.classes.expanded);
    this.element.classList.add(this.classes.collapsed);
  }

  _showMenu() {
    this.togglerButtonTarget.ariaExpanded = true;
    this.containerTarget.removeAttribute('hidden');
    this.navigationTarget.classList.add(this.classes.invisible);

    if (!this._positioner) {
      import('./mixins/use_dropdown_positioner').then(({DropdownPositioner}) => {
        this._positioner = new DropdownPositioner(this.togglerButtonTarget, this.navigationTarget, {
          placement: 'bottom-end',
          verticalCompensation: 10,
          offsetPx: 0,
        });
        this._positioner.init();
      });
      bodyOverflowHidden(this);
    }
  }

  _hideMenu() {
    if (this._positioner) {
      this._positioner.destroy();
      this._positioner = null;
    }
    this.togglerButtonTarget.ariaExpanded = false;
    this.containerTarget.setAttribute('hidden', '');
  }

  _showToggler() {
    this.togglerTarget.removeAttribute('hidden');
    this.togglerTarget.style.height = this.togglerHeight + 'px';
  }

  _hideToggler() {
    this.togglerTarget.setAttribute('hidden', '');
  }

  _addClickOutsideListener() {
    document.addEventListener('click', this.clickOutsideListener);
  }

  _removeClickOutsideListener() {
    document.removeEventListener('click', this.clickOutsideListener);
  }

  _terminateOSObservation() {
    this.scrollBarsObserver && this.scrollBarsObserver.disconnect();
    this.scrollBarsObserverTimeout && clearTimeout(this.scrollBarsObserverTimeout);
    this.scrollBarsObserver = null;
    this.scrollBarsObserverTimeout = null;
  }

  onResizeChange() {
    this._restartMenu();
  }

  onZoomChange() {
    this._restartMenu();
  }

  _restartMenu() {
    this._hideMenu();
    this._setMenuState();
  }
}
